import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { C } from './CamerasStyles';
import {
  SNOOZE_MINUTES,
  extendedNote,
  panelFoot,
  pillView,
  pillWhy,
  presenceRows,
  pressEndSnooze,
  pressImHere,
  pressSnooze,
  shopClock,
} from '../../features/security/nightWatch';

// The night watch beside the camera-health pill (jobq-api /security/status, polled by the page): one
// line saying whether the shop is being watched and why not, and a panel with who counts as here, the
// Pi's last word, and the two buttons (I'm here / Snooze). Wording and rules: features/security.

export const WATCH_TONE = { bad: C.bad, warn: C.warn, watch: C.work, muted: C.faint };

export function ShieldIcon({ color = 'currentColor', size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
    </svg>
  );
}

/** Whole shop / one camera, 15–120 min, why: the snooze form (desktop panel + Alerts rows). */
export function SnoozeForm({ cameras, cameraKey = null, busy, onSubmit, onCancel }) {
  const [scope, setScope] = useState(cameraKey ? 'camera' : 'whole');
  const [cam, setCam] = useState(cameraKey || '');
  const [minutes, setMinutes] = useState(30);
  const [reason, setReason] = useState('');
  const until = new Date(Date.now() + minutes * 60000);
  const ready = scope === 'whole' || !!cam;
  return (
    <div style={{ display: 'grid', gap: 8, borderTop: `1px solid ${C.border}`, paddingTop: 10 }} data-testid="snooze-form">
      <span style={{ fontSize: 12, color: C.muted }}>What</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <button type="button" className="jqc-chip" aria-pressed={scope === 'whole'} onClick={() => setScope('whole')}>Whole shop</button>
        <button type="button" className="jqc-chip" aria-pressed={scope === 'camera'} onClick={() => setScope('camera')}>One camera</button>
      </div>
      {scope === 'camera' && (
        <select aria-label="Camera to snooze" value={cam} onChange={(e) => setCam(e.target.value)}
                style={{ minHeight: 34, borderRadius: 8, border: `1px solid ${C.borderStrong}`, background: C.bg, color: C.text, padding: '0 8px' }}>
          <option value="">— pick a camera —</option>
          {(cameras || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      )}
      <span style={{ fontSize: 12, color: C.muted }}>For</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {SNOOZE_MINUTES.map((m) => (
          <button key={m} type="button" className="jqc-chip" aria-pressed={minutes === m} onClick={() => setMinutes(m)}>
            {m < 60 ? `${m} min` : `${m / 60} h`}
          </button>
        ))}
      </div>
      <label style={{ fontSize: 12, color: C.muted, display: 'grid', gap: 4 }}>
        Why (optional)
        <input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="Kevin dropping off the trailer"
               style={{ minHeight: 34, borderRadius: 8, border: `1px solid ${C.borderStrong}`, background: C.bg, color: C.text, padding: '0 10px' }} />
      </label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="jqc-btn" disabled={busy || !ready}
                style={{ background: C.text, color: C.bg, borderColor: C.text }}
                onClick={() => onSubmit({ scope, cameraKey: scope === 'camera' ? cam : null, minutes, reason })}>
          Snooze until {shopClock(until)}
        </button>
        <button type="button" className="jqc-btn" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      <span style={{ fontSize: 12, color: C.muted }}>Next: your PIN. Slack gets a line now and when it ends.</span>
    </div>
  );
}

export default function SecurityPill({ status, error, cameras, ui, onChanged }) {
  const [open, setOpen] = useState(false);
  const [snoozing, setSnoozing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [, setTick] = useState(0);
  const wrapRef = useRef(null);
  // A refused / failed read with nothing loaded yet says so (never "Night watch…" forever).
  const v = !status && error ? { state: 'error', tone: 'warn', main: 'Night watch', side: 'not loaded' } : pillView(status);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A held alert counts down by the second.
  useEffect(() => {
    if (v.state !== 'holding') return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [v.state]);

  const act = async (fn, done) => {
    setBusy(true);
    setNote('');
    try {
      const r = await fn();
      if (r && r.ok) {
        setNote(done(r.body || {}));
        setSnoozing(false);
        if (onChanged) onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const tone = WATCH_TONE[v.tone] || C.faint;
  const rows = presenceRows(status);
  const snooze = status?.snooze;
  const manualHere = (status?.armed?.present || []).some((p) => p.source === 'manual');
  const watchable = (cameras || []).filter((c) => !(status?.watcher?.cameras_ignored || []).includes(c.key));

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" className="jqc-btn" aria-expanded={open} aria-label={`Night watch: ${v.main}${v.side ? `, ${v.side}` : ''}`}
              onClick={() => setOpen((o) => !o)} data-testid="security-pill">
        {v.tone === 'bad' ? <span className="jqc-dot" style={{ background: tone }} /> : <ShieldIcon color={tone} />}
        <span style={{ color: v.tone === 'watch' ? C.text : tone }}>{v.main}</span>
        {v.side && <span style={{ color: C.muted, fontWeight: 400 }}>· {v.side}</span>}
      </button>
      {open && (
        <div className="jqc-pop" role="dialog" aria-label="Night watch detail" style={{ width: 'min(460px, 92vw)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 6 }}>
            <ShieldIcon color={tone} size={16} />
            Night watch · {v.main.toLowerCase()}
          </div>
          {error && <div style={{ color: C.bad, fontSize: 13, marginBottom: 6 }}>{error}</div>}
          <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 8 }}>{pillWhy(status)}</div>
          {rows.map((r) => (
            <div key={r.key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '6px 0', borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                {r.who}
                <span style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginLeft: 6 }}>{r.source}</span>
              </span>
              <span style={{ color: r.long ? C.warn : r.ignored ? C.faint : C.muted, fontSize: 12, textAlign: 'right' }}>
                {r.since}{r.since && r.until ? <br /> : null}{r.until}
              </span>
            </div>
          ))}
          {!rows.length && status && (
            <div style={{ padding: '6px 0', borderTop: `1px solid ${C.border}`, fontSize: 13, color: C.muted }}>Nobody counts as here.</div>
          )}
          {(status?.camera_snoozes || []).map((z) => (
            <div key={z.snooze_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
              <span style={{ flex: 1 }}>{(cameras || []).find((c) => c.key === z.camera_key)?.label || z.camera_key} snoozed by {z.by}{extendedNote(z)} until {shopClock(z.until)}</span>
              <button type="button" className="jqc-btn" disabled={busy}
                      onClick={() => act(() => pressEndSnooze({ ui, snoozeId: z.snooze_id }), () => 'Watching that camera again.')}>
                Watch again
              </button>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 4, fontSize: 12, color: C.muted, display: 'grid', gap: 3 }}>
            {panelFoot(status).map((l) => <span key={l}>{l}</span>)}
            <span>
              <Link to="/doors" style={{ color: C.textSoft }}>Doors</Link>: the door alarm, each door, and its alerts.
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 10 }}>
            <button type="button" className="jqc-btn" disabled={busy}
                    style={v.state === 'holding' ? { background: C.text, color: C.bg, borderColor: C.text } : undefined}
                    onClick={() => act(() => pressImHere({ ui, event: 'arrive' }), (b) => `${b.person || 'You'} count${b.person ? 's' : ''} as here now.`)}>
              I'm here
            </button>
            {manualHere && (
              <button type="button" className="jqc-btn" disabled={busy}
                      onClick={() => act(() => pressImHere({ ui, event: 'leave' }), (b) => `${b.person || 'You'} left.`)}>
                I'm leaving
              </button>
            )}
            {snooze ? (
              <>
                <button type="button" className="jqc-btn" disabled={busy} style={{ borderColor: '#6b5021', color: '#ffd38a' }}
                        onClick={() => act(() => pressEndSnooze({ ui, snoozeId: snooze.snooze_id }), () => 'Armed again.')}>
                  Arm now
                </button>
                <button type="button" className="jqc-btn" disabled={busy}
                        onClick={() => act(() => pressSnooze({ ui, scope: 'whole', minutes: 30 }),
                          (b) => `Snoozed until ${shopClock(b.snooze?.until)}${b.capped ? ' (the most it allows)' : ''}.`)}>
                  +30 min
                </button>
              </>
            ) : (
              <button type="button" className="jqc-btn" disabled={busy} aria-expanded={snoozing} onClick={() => setSnoozing((s) => !s)}>
                Snooze ▾
              </button>
            )}
          </div>
          {snoozing && (
            <SnoozeForm cameras={watchable} busy={busy} onCancel={() => setSnoozing(false)}
                        onSubmit={(f) => act(() => pressSnooze({ ui, ...f }),
                          (b) => `Snoozed ${f.scope === 'whole' ? 'the whole shop' : 'that camera'} until ${shopClock(b.snooze?.until)}.`)} />
          )}
          {note && <div role="status" style={{ fontSize: 13, color: C.live, paddingTop: 8 }}>{note}</div>}
        </div>
      )}
    </div>
  );
}
